import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { usePayment, type PayMode } from '@/features/payment/presentation/hooks/usePayment';
import { paymentBuild } from '@/features/payment/config/payment.build';
import type { AppDispatch } from '@/app/store/store';
import { CheckoutFlowLayout } from '@/app/Layouts/CheckoutFlowLayout';
import CheckoutHeader from '@/shared/features/header/presentation/components/CheckoutHeader';
import UpiPayInfo from '@/features/payment/presentation/components/UpiPayInfo';
import CardPaymentForm from '@/features/payment/presentation/components/CardPaymentForm';
import NetBanking from '@/features/payment/presentation/components/NetBanking';
import OrderSummary from '@/features/payment/presentation/components/OrderSummary';
import { cn } from '@/shared/utils/cn';
import { Button } from '@/shared/widgets/button';
import { 
  CreditCard, 
  Landmark,
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/widgets/accordion';
import { CloseIcon, UPIIcon } from '@/features/payment/presentation/components/icons';
import { formatPrice } from '@/shared/utils/priceFormatter';
import { getPublicAssetUrl } from '@/shared/utils/publicAssetUrl';

interface PayViaLinkPageProps {
  paymentContextQueryOverride?: string;
}

const timeoutIllustrationUrl = getPublicAssetUrl('assets/Timeout-icon.svg');

const BluestoneMark: React.FC = () => (
  <svg
    viewBox="0 0 32 32"
    className="h-9 w-9 text-[#011E38]"
    aria-label="Bluestone"
  >
    <path
      d="M21.142 26.667c-0.001 0-0.002 0-0.003 0-1.205 0-2.183 0.976-2.185 2.181v0c0.001 1.206 0.979 2.184 2.185 2.184 0.593 0 1.132-0.237 1.525-0.621l-0 0c0.409-0.395 0.664-0.949 0.664-1.564-0.002-1.205-0.979-2.182-2.185-2.182-0.001 0-0.002 0-0.002 0h0zM15.789 7.786c-3.371-3.692-1.854-6.649-0.946-7.786-3.308 1.428-6.674 5.807-1.42 11.705l1.236 1.388c2.444 2.748 8.009 9.005 12.051 13.572 0.156-0.17 0.309-0.344 0.458-0.521 0.634-0.741 1.020-1.711 1.020-2.772 0-1.099-0.415-2.101-1.096-2.859l-11.303-12.73zM27.005 4.787c-0.212-0.83-0.681-1.653-1.235-2.307-1.619-1.914-4.872-2.479-4.872-2.479s0.654 0.376 1.327 1.171c0.852 1.006 1.654 2.663 0.87 4.929-0.156 0.482-0.401 0.948-0.401 1.468 0 1.176 0.947 2.13 2.115 2.13 0.342 0 0.682-0.084 0.984-0.244 0.256-0.136 0.473-0.318 0.653-0.53 0.408-0.481 0.634-1.118 0.717-1.742 0.031-0.235 0.049-0.507 0.049-0.784 0-0.573-0.076-1.128-0.219-1.655l0.010 0.044zM11.672 25.21c-0.374 2.814-1.992 5.178-4.253 6.66 0.425 0.083 0.855 0.129 1.287 0.129 3.819 0 6.665-2.748 7.537-5.94 0.034-0.121 0.064-0.243 0.092-0.366l0.035-0.16c0.052-0.237 0.096-0.528 0.123-0.824l0.002-0.028 0.013-0.172c0.008-0.113 0.014-0.25 0.015-0.389l0-0.005c0.061-4.020-3.030-7.531-7.283-8.052-0.316-0.041-0.683-0.064-1.054-0.064-0.316 0-0.628 0.017-0.935 0.049l0.038-0.003c3.059 1.94 4.877 5.437 4.383 9.164zM4.374 30.060v-24.727c-1.865 1.452-3.349 3.305-4.336 5.436l-0.038 0.091v15.486c0.326 0.378 2.071 2.336 4.374 3.715z"
      fill="currentColor"
    />
  </svg>
);

const formatRemainingTime = (expiresAt?: number): string => {
  if (!expiresAt) return '--:--';

  const remainingSeconds = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const ExpiryCountdownBadge: React.FC<{ expiresAt?: number; className?: string }> = ({
  expiresAt,
  className,
}) => {
  const [timeLeft, setTimeLeft] = React.useState(() => formatRemainingTime(expiresAt));

  React.useEffect(() => {
    setTimeLeft(formatRemainingTime(expiresAt));

    if (!expiresAt) return undefined;

    const timerId = window.setInterval(() => {
      setTimeLeft(formatRemainingTime(expiresAt));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [expiresAt]);

  return (
    <div
      className={cn(
        'rounded-full bg-[#F2F2F2] px-3 py-1 text-center text-xs font-extrabold tabular-nums text-[#011E38]',
        className
      )}
    >
      {timeLeft}
    </div>
  );
};

const MobileCheckoutHeader: React.FC<{ amount: number; expiresAt?: number }> = ({
  amount,
  expiresAt,
}) => {
  return (
    <header className="sticky top-0 z-50 grid grid-cols-[72px_minmax(80px,1fr)_104px] items-center bg-white px-3 py-3 shadow-[0_1px_0_rgba(1,30,56,0.08)] md:hidden">
      <div className="flex items-center">
        <BluestoneMark />
      </div>

      <ExpiryCountdownBadge expiresAt={expiresAt} className="justify-self-center" />

      <div className="justify-self-end text-right leading-tight">
        <div className="text-xs font-extrabold text-[#011E38]">Order Total</div>
        <div className="mt-1 text-lg font-extrabold text-[#303030]">{formatPrice(amount)}</div>
      </div>
    </header>
  );
};

const PaymentTimeoutState: React.FC = () => (
  <div className="min-h-screen bg-white px-6 py-16">
    <div className="mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-lg flex-col items-center justify-center text-center">
      <img
        src={timeoutIllustrationUrl}
        alt=""
        className="mb-8 h-auto w-[170px] max-w-full select-none md:w-[200px]"
        aria-hidden="true"
      />

      <h1 className="text-[22px] font-extrabold leading-tight text-[#011E38] md:text-[24px]">
        Request expired. Please try again.
      </h1>

      <p className="mt-5 text-base font-medium leading-6 text-[#011E38] md:text-lg md:leading-7">
        Please retry to complete your payment
      </p>
    </div>
  </div>
);

const UpiIntentPendingOverlay: React.FC<{ amount: number }> = ({ amount }) => (
  <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-white px-6 text-center">
    <div className="mx-auto flex w-full max-w-md flex-col items-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#5D57D9]">
        <div className="h-7 w-7 animate-spin rounded-full border-[3px] border-[#25E779] border-t-transparent" />
      </div>

      <h1 className="mt-10 max-w-[360px] text-[24px] font-extrabold leading-[1.65] text-[#011E38]">
        Please go to your UPI app to complete your payment of {formatPrice(amount)}.
      </h1>

      <p className="mt-6 text-base font-medium text-[#011E38]">
        Note: Please don&apos;t refresh or close the app
      </p>
    </div>
  </div>
);

export const PayViaLinkPage: React.FC<PayViaLinkPageProps> = ({
  paymentContextQueryOverride,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => paymentBuild(dispatch), [dispatch]);
  const {
    paymentMode,
    setPaymentMode,
    selectedBank,
    cardName,
    setCardName,
    cardNumber,
    cardExpiry,
    cardCvv,
    setCardCvv,
    saveCard,
    setSaveCard,
    cardNetwork,
    cardNumberError,
    cvvLength,
    upiMode,
    handleUpiModeChange,
    showMobileBankDrawer,
    setShowMobileBankDrawer,
    netBankingBanks,
    details,
    isLoading,
    detailsError,
    isExpired,
    isPaymentSubmitting,
    isUpiIntentPending,
    paymentError,
    handleCardNumberChange,
    handleExpiryChange,
    handlePayNow,
    handleSelectBank,
    handleSelectBankMobile,
    isCardFormValid,
    isExpiryInvalid,
  } = usePayment({ service, paymentContextQueryOverride });

  const [activeAccordionItem, setActiveAccordionItem] = React.useState<string>('upi');
 

  React.useEffect(() => {
    setActiveAccordionItem(paymentMode);
  }, [paymentMode]);

  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6FAFF]">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin h-10 w-10 border-4 border-[#011E38] border-t-transparent rounded-full" />
          <span className="text-sm font-semibold text-gray-500">Loading order details...</span>
        </div>
      </div>
    );
  }

  if (detailsError) {
  return (
    <CheckoutFlowLayout className="bg-white">
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <p
          role="alert"
          className="w-full max-w-2xl text-center text-2xl font-bold text-red-700"
        >
          {detailsError}
        </p>
      </main>
    </CheckoutFlowLayout>
  );
}

  if (!details) return null;

  if (isExpired) {
    return <PaymentTimeoutState />;
  }

  const desktopPaymentTabBaseClass =
    'relative flex h-[72px] cursor-pointer items-center gap-3 border-b border-[#DADADA] px-4 text-left text-sm font-extrabold tracking-[0.02em] transition-colors whitespace-nowrap';
  const desktopPaymentTabActiveClass =
    'bg-white text-[#4A4A4A] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#00875A] after:content-[""]';
  const desktopPaymentTabIdleClass = 'bg-white text-[#4A4A4A] hover:bg-gray-50';
  const expiresAt = details.expiresAt;

  return (
    <CheckoutFlowLayout
      className="flex min-h-screen flex-col bg-[#EDEDED] md:bg-white"
      showHeader={false}
    >
        <MobileCheckoutHeader amount={details.amount} expiresAt={expiresAt} />
        <CheckoutHeader
          className="hidden md:flex"
          centerContent={<ExpiryCountdownBadge expiresAt={expiresAt} className="px-4 py-1.5 text-sm" />}
        />

        {/* Main content grid */}
        <main className="relative flex-1 bg-[#EDEDED] md:border-t md:border-[#F2F2F2] md:bg-white md:shadow-[inset_0_10px_18px_-18px_rgba(1,30,56,0.28)] lg:before:pointer-events-none lg:before:absolute lg:before:inset-y-0 lg:before:left-[calc(50%+186px)] lg:before:w-px lg:before:bg-[#E5E5E5] lg:before:content-['']">
          <div className="relative z-10 mx-auto max-w-6xl px-4 pt-6 pb-12 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left panel - Payment modes selector */}
          <div className="lg:col-span-8 space-y-6">
            {paymentError && (
              <div className="bg-red-50 border border-red-100 rounded-md px-4 py-3 text-sm font-semibold text-red-600">
                {paymentError}
              </div>
            )}
            
            {/* Desktop Tabs layout */}
            <div className="hidden overflow-hidden rounded-lg border border-[#D8D8D8] bg-white shadow-[0_12px_30px_rgba(1,30,56,0.12)] md:block">
              <div className="flex min-h-[460px]">
                {/* Tabs selection sidebar */}
                <div className="flex w-[38%] flex-col justify-start border-r border-[#DADADA] bg-white">
                  <div
                    onClick={() => setPaymentMode('upi')}
                    className={cn(
                      desktopPaymentTabBaseClass,
                      paymentMode === 'upi' ? desktopPaymentTabActiveClass : desktopPaymentTabIdleClass
                    )}
                  >
                    <UPIIcon className="h-auto w-5 object-contain" />
                    <span>Pay via UPI</span>
                  </div>
                  <div
                    onClick={() => setPaymentMode('card')}
                    className={cn(
                      desktopPaymentTabBaseClass,
                      paymentMode === 'card' ? desktopPaymentTabActiveClass : desktopPaymentTabIdleClass
                    )}
                  >
                    <CreditCard className="h-4 w-4 shrink-0" color="#ef5d35" strokeWidth={2} />
                    <span>New Credit or Debit Card</span>
                  </div>
                  <div
                    onClick={() => setPaymentMode('netbanking')}
                    className={cn(
                      desktopPaymentTabBaseClass,
                      paymentMode === 'netbanking' ? desktopPaymentTabActiveClass : desktopPaymentTabIdleClass
                    )}
                  >
                    <Landmark className="h-5 w-5 shrink-0" color="#ef5d35" strokeWidth={2} />
                    <span>Net Banking</span>
                  </div>
                </div>

                {/* Form Panels */}
                <div className="flex w-[62%] flex-col px-8 py-8">
	                  <div className="space-y-4">
	                    {paymentMode === 'upi' && (
	                      <UpiPayInfo
	                        mode={upiMode}
	                        onModeChange={handleUpiModeChange}
                        variant="desktop"
                        disabled={isPaymentSubmitting}
                      />
                    )}

                    {paymentMode === 'card' && (
                      <CardPaymentForm
                        formId="payvialink-card-form-desktop"
                        cardName={cardName}
                        cardNumber={cardNumber}
                        cardExpiry={cardExpiry}
                        cardCvv={cardCvv}
                        saveCard={saveCard}
                        isBankOfferSelected={details.isBankOfferSelected}
                        onCardNameChange={setCardName}
                        onCardNumberChange={handleCardNumberChange}
                        onExpiryChange={handleExpiryChange}
                        onCvvChange={setCardCvv}
                        onSaveCardChange={setSaveCard}
                        cardNetwork={cardNetwork}
	                        cardNumberError={cardNumberError || undefined}
	                        cvvLength={cvvLength}
	                        isExpiryInvalid={isExpiryInvalid}
	                        variant="desktop"
	                      />
	                    )}

	                    {paymentMode === 'netbanking' && (
	                      <NetBanking
	                        onSelectBank={(code, name) => {
	                          handleSelectBank(code, name);
	                          handlePayNow('netbanking', { code, name });
	                        }}
	                        selectedBankCode={selectedBank?.code}
	                        banks={netBankingBanks}
	                      />
	                    )}
	                  </div>
	                </div>
	              </div>
	            </div>

            {paymentMode !== 'netbanking' && (
              <div className="hidden justify-end pt-5 md:flex">
                <Button
                  variant="primary"
                  onClick={() => handlePayNow()}
                  disabled={
                    isPaymentSubmitting ||
                    (paymentMode === 'card' && !isCardFormValid)
                  }
                  className="h-[52px] w-[340px] rounded-[5px] bg-[#F89B89] text-[20px] font-medium shadow-none hover:bg-[#F47F69]"
                >
                  {isPaymentSubmitting ? 'Processing...' : 'Pay now'}
                </Button>
              </div>
            )}

            {/* Mobile Accordion layout */}
            <div className="md:hidden space-y-4">
              <Accordion
                type="single"
                collapsible
                value={activeAccordionItem}
                onValueChange={(val) => {
                  setActiveAccordionItem(val || '');
                  if (val) {
                    setPaymentMode(val as PayMode);
                    if (val === 'netbanking') {
                      setShowMobileBankDrawer(true);
                    }
                  }
                }}
                className="w-full space-y-3"
              >
                
                <AccordionItem
                  value="upi"
                  className="bg-white border border-[#0000001A] rounded-lg overflow-hidden shadow-sm"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-sm text-[#011E38]">
                    <div className="flex items-center space-x-3">
                      <UPIIcon />
                      <span>Pay via UPI</span>
                    </div>
                  </AccordionTrigger>
	                  <AccordionContent className="px-6 pb-6 pt-0 border-t border-gray-50">
	                    <div className="pt-4 space-y-4">
	                      <UpiPayInfo
	                        mode={upiMode}
	                        onModeChange={handleUpiModeChange}
                        variant="mobile"
                        onPay={(selectedUpiMode) => handlePayNow('upi', undefined, selectedUpiMode)}
                        disabled={isPaymentSubmitting}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value="card"
                  className="bg-white border border-[#0000001A] rounded-lg overflow-hidden shadow-sm"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-sm text-[#011E38]">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="h-5 w-5 text-[#EF5D35]" />
                      <span>New credit or debit card</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-4 border-t border-gray-50">
                    <CardPaymentForm
                      formId="payvialink-card-form-mobile"
                      cardName={cardName}
                      cardNumber={cardNumber}
                      cardExpiry={cardExpiry}
                      cardCvv={cardCvv}
                      saveCard={saveCard}
                      isBankOfferSelected={details.isBankOfferSelected}
                      onCardNameChange={setCardName}
                      onCardNumberChange={handleCardNumberChange}
                      onExpiryChange={handleExpiryChange}
                      onCvvChange={setCardCvv}
                      onSaveCardChange={setSaveCard}
                      cardNetwork={cardNetwork}
                      cardNumberError={cardNumberError || undefined}
                      cvvLength={cvvLength}
                      isExpiryInvalid={isExpiryInvalid}
                    />
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value="netbanking"
                  className="bg-white border border-[#0000001A] rounded-lg overflow-hidden shadow-sm"
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline font-bold text-sm text-[#011E38]">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center space-x-3">
                        <Landmark className="h-4 w-4" />
                        <span>Net banking</span>
                      </div>
                      {selectedBank && (
                        <span className="text-xs font-semibold text-[#458024] bg-[#609B3E1A] px-2 py-0.5 rounded">
                          {selectedBank.name}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-6 pt-4 border-t border-gray-50">
                    <div className="flex flex-col items-start justify-center py-2 text-center">
                      {selectedBank ? (
                        <div className="space-y-1">
                          <p className="text-xs text-gray-500">
                            Selected Bank: <span className="text-xs text-[#f45b4d] font-bold ">{selectedBank.name}</span>
                          </p>
                        </div>
                      ) : (
                        <p
                          className=" text-xs text-gray-500 "
                        >
                          Select bank from list
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

          </div>

          {/* Right panel - Order Summary */}
          <div className="hidden lg:col-span-4 lg:block">
            <OrderSummary details={details} />
          </div>
        </div>
          </div>
        </main>

      {isUpiIntentPending && <UpiIntentPendingOverlay amount={details.amount} />}

      {/* Mobile Fixed Bottom Action Bar */}
      <div
        className={cn(
          "md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex flex-col shadow-[0_-2px_10px_rgba(0,0,0,0.05)] z-50 transition-all duration-300 ease-in-out transform",
          activeAccordionItem === 'card'
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0 pointer-events-none"
        )}
      >
        

        <div className="px-4 py-3 w-full">
          <button
            onClick={() => {
              handlePayNow('card')
            }}
            disabled={!isCardFormValid || isPaymentSubmitting}
            className="relative w-full py-4 px-5 bg-[#f45b4d] hover:bg-[#e14c3e] text-white font-bold text-sm rounded shadow-sm overflow-hidden flex items-center justify-between select-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f45b4d] focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
          >
            
            <span className="relative z-10">{isPaymentSubmitting ? 'Processing...' : 'Proceed'}</span>
            <span className="relative z-10">{formatPrice(details.amount)}</span>
          </button>
        </div>
      </div>

      {/* Bottom Drawer for Mobile Net Banking selection */}
      <div
        className={cn(
          "md:hidden fixed inset-0 bg-black/60 z-[1000] flex flex-col justify-end transition-opacity duration-300 ease-in-out",
          showMobileBankDrawer ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      >
        <div
          className={cn(
            "bg-white rounded-t-xl max-h-[85vh] flex flex-col shadow-2xl transition-transform duration-300 ease-in-out transform",
            showMobileBankDrawer ? "translate-y-0" : "translate-y-full"
          )}
        >
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100">
            <span className="text-sm font-bold text-[#011E38]">Select Bank</span>
            <Button
              onClick={() => {
                setShowMobileBankDrawer(false);
                setActiveAccordionItem('');
              }}
              className="p-1 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
              variant="primary"
            >
              <CloseIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="p-4 overflow-y-auto flex-1">
            <NetBanking
              onSelectBank={(code, name) => {
                handleSelectBankMobile(code, name);
                setActiveAccordionItem('');
                handlePayNow('netbanking', { code, name });
              }}
              selectedBankCode={selectedBank?.code}
              isMobileView={true}
              banks={netBankingBanks}
            />
          </div>
        </div>
      </div>
    </CheckoutFlowLayout>
  );
};

export default PayViaLinkPage;
